import React, { useState, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../supabaseClient'; // Asegúrate de que la ruta sea correcta

const PurchaseSummary = forwardRef(({
  quantity,
  ticketPrice = 50,
  formData,
  paymentData,
  selectedMethod,
  uploadedFile,
  onSubmit,
  onProcessStatus = () => {}, // callback para informar resultado al padre
  errors = {},
  selectedTickets = new Set(),
  raffleId,
  canProceed = true,
  isProcessing: parentProcessing = false // ahora recibimos sólo isProcessing desde el padre
}, ref) => {
  const [isProcessing, setIsProcessing] = useState(false); // Estado interno para bloquear doble click
  const [serverError, setServerError] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const totalAmount = quantity * ticketPrice;

  const isFormValid = useCallback(() => {
    const hasUserInfo = formData?.cedula && formData?.name && formData?.email && formData?.phone;
    if (!hasUserInfo) return false;

    if (!selectedMethod) return false;

    let hasPaymentDetails = false;
    switch (selectedMethod) {
      case 'pago_movil':
        hasPaymentDetails = paymentData?.bankIssuer && paymentData?.reference && paymentData?.amount;
        break;
      case 'zelle':
        hasPaymentDetails = paymentData?.zelleConfirmation && paymentData?.amount;
        break;
      case 'binance':
        hasPaymentDetails = paymentData?.transactionHash && paymentData?.network && paymentData?.cryptoAmount && paymentData?.amount;
        break;
      default:
        return false;
    }

    if (!hasPaymentDetails) return false;

    const hasFile = uploadedFile && (uploadedFile.file || uploadedFile.name || uploadedFile.url);
    if (!hasFile) return false;

    return true;
  }, [formData, paymentData, selectedMethod, uploadedFile]);

  const getPaymentMethodName = () => {
    switch (selectedMethod) {
      case 'pago_movil': return 'Pago Móvil';
      case 'zelle': return 'Zelle';
      case 'binance': return 'Binance Pay';
      default: return 'No seleccionado';
    }
  };

  const getCompletionPercentage = () => {
    let completed = 0;
    const total = 4;
    if (formData?.cedula && formData?.name && formData?.email && formData?.phone) completed++;
    if (selectedMethod) completed++;
    if (paymentData?.amount) completed++;
    if (uploadedFile) completed++;
    return Math.round((completed / total) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  // Función que realiza todo el procesamiento de compra
  const handleProcessPurchase = useCallback(async () => {
    setServerError(null);

    if (!canProceed) {
      setServerError('No cumples los requisitos mínimos para proceder con la compra.');
      onProcessStatus('error');
      return;
    }
    if (parentProcessing || isProcessing) {
      console.warn('Procesamiento bloqueado: ya hay un envío en curso');
      return;
    }
    if (!isFormValid()) {
      setServerError('Faltan datos requeridos en el formulario o en el comprobante de pago.');
      onProcessStatus('error');
      return;
    }
    if (!raffleId) {
      setServerError('Error interno: no se encontró el sorteo activo. Intenta recargar la página.');
      onProcessStatus('error');
      return;
    }
    if (!selectedTickets || !(selectedTickets instanceof Set) || selectedTickets.size === 0) {
      setServerError('No hay tickets seleccionados. Selecciona los números que deseas adquirir.');
      onProcessStatus('error');
      return;
    }

    setIsProcessing(true);
    try {
      // 1) Obtener los rows de tickets (incluyendo sus ids) según los números seleccionados
      const ticketNumbersArray = Array.from(selectedTickets).map(n => Number(n));
      const { data: ticketRows, error: ticketRowsError } = await supabase
        .from('tickets')
        .select('id, numero, estado')
        .in('numero', ticketNumbersArray)
        .eq('raffle_id', raffleId);

      if (ticketRowsError) {
        console.error('❌ Error al consultar tickets antes de la compra:', ticketRowsError);
        setServerError('Error al verificar los tickets seleccionados. Intenta nuevamente.');
        onProcessStatus('error');
        return;
      }

      // 2) Validaciones: que todos existan y estén disponibles
      const foundNumbers = (ticketRows || []).map(t => Number(t.numero));
      const missingNumbers = ticketNumbersArray.filter(n => !foundNumbers.includes(n));
      if (missingNumbers.length > 0) {
        console.error('❌ Algunos números no existen en la base de datos para este sorteo:', missingNumbers);
        setServerError(`Algunos boletos seleccionados no están disponibles: ${missingNumbers.join(', ')}`);
        onProcessStatus('error');
        return;
      }

      const notAvailable = (ticketRows || []).filter(t => t.estado !== 'disponible');
      if (notAvailable.length > 0) {
        console.error('❌ Algunos tickets ya no están disponibles:', notAvailable);
        setServerError('Algunos boletos ya fueron vendidos. Recarga la lista de tickets y vuelve a intentarlo.');
        onProcessStatus('error');
        return;
      }

      // 3) Extraer números reales de tickets y preparar purchaseData
      const numeros = ticketRows.map(t => Number(t.numero)); // Extraemos los números en lugar de los IDs
      
      console.log('🔢 Números de tickets a procesar:', numeros);
      console.log('🗃️ Tipo de datos de numeros:', typeof numeros, Array.isArray(numeros));

      const purchaseUUID = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `purchase-${Date.now()}`;
      const purchaseData = {
        uuid: purchaseUUID,
        raffle_id: raffleId,
        usuario_id: 'anon', // adapta si tienes el usuario real
        nombre_completo: formData.name,
        cedula: formData.cedula,
        email: formData.email,
        telefono: formData.phone,
        cantidad_boletos: quantity,
        total_pagado: totalAmount,
        metodo_pago: selectedMethod,
        estado: 'pendiente',
        numeros: numeros // Array de números
      };

      // 4) Insertar la compra incluyendo numeros
      console.log('📝 Datos de compra a insertar:', purchaseData);
      
      const { data: purchaseInsertData, error: purchaseInsertError } = await supabase
        .from('purchases')
        .insert([purchaseData])
        .select('id');

      if (purchaseInsertError) {
        console.error('❌ Error al insertar en purchases:', purchaseInsertError);
        console.error('❌ Código de error:', purchaseInsertError.code);
        console.error('❌ Mensaje:', purchaseInsertError.message);
        console.error('❌ Detalles:', purchaseInsertError.details);
        
        // Mensaje más específico basado en el error
        let errorMessage = 'Error al crear la compra. ';
        if (purchaseInsertError.code === '42883') {
          errorMessage += 'Error de base de datos. Contacta al soporte técnico.';
        } else if (purchaseInsertError.message?.includes('404')) {
          errorMessage += 'Tabla no encontrada. Verifica la configuración de la base de datos.';
        } else {
          errorMessage += 'Intenta nuevamente más tarde.';
        }
        
        setServerError(errorMessage);
        onProcessStatus('error');
        return;
      }

      const purchase = purchaseInsertData?.[0];
      if (!purchase?.id) {
        console.error('⚠️ La compra se insertó pero no se recibió el ID:', purchaseInsertData);
        setServerError('La compra no pudo confirmarse correctamente. Contacta soporte.');
        onProcessStatus('error');
        return;
      }

      // Éxito total - el trigger se encarga de actualizar los tickets
      console.log(`✅ Compra registrada con números: ${numeros.join(', ')}. El trigger actualizará los tickets.`);
      onProcessStatus('success');
      setShowSuccessPopup(true); // Mostrar popup en lugar de llamar onSubmit

    } catch (err) {
      console.error('🚨 Error general al procesar la compra:', err);
      setServerError('Ocurrió un error inesperado durante la compra. Intenta nuevamente más tarde.');
      onProcessStatus('error');
    } finally {
      setIsProcessing(false);
    }
  }, [
    canProceed,
    parentProcessing,
    isProcessing,
    isFormValid,
    raffleId,
    selectedTickets,
    quantity,
    totalAmount,
    selectedMethod,
    formData,
    paymentData,
    uploadedFile,
    onProcessStatus,
    onSubmit
  ]);

  // Exponemos process() via ref para que PurchaseFlow pueda iniciarlo desde el botón principal
  useImperativeHandle(ref, () => ({
    process: handleProcessPurchase
  }));

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-luxury sticky top-24">
      <div className="flex items-center space-x-2 mb-6">
        <div className="p-2 bg-secondary/10 rounded-lg">
          <Icon name="ShoppingCart" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-semibold text-gray-800">Resumen de Compra</h3>
          <p className="text-sm text-gray-800 font-caption">Verifica tu información antes de procesar</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-800 font-caption">Progreso</span>
          <span className="text-secondary font-mono font-bold">{completionPercentage}%</span>
        </div>
        <div className="w-full bg-muted/30 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-secondary to-success h-2 rounded-full transition-all duration-500"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-body font-medium text-gray-800 text-sm mb-3">Detalles del Pedido</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-800">Cantidad:</span>
              <span className="font-mono text-gray-800">{quantity} boletos</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-800">Precio unitario:</span>
              <span className="font-mono text-gray-800">Bs. {ticketPrice?.toLocaleString()}</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex justify-between">
                <span className="font-body font-medium text-gray-800">Total:</span>
                <span className="font-mono font-bold text-secondary text-lg">
                  Bs. {totalAmount?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-800">Información personal</span>
            {formData?.cedula && formData?.name && formData?.email && formData?.phone ? (
              <Icon name="CheckCircle" size={16} className="text-success" />
            ) : (
              <Icon name="Circle" size={16} className="text-muted-foreground" />
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-800">Método de pago</span>
            {selectedMethod ? (
              <div className="flex items-center space-x-1">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-xs text-gray-800">{getPaymentMethodName()}</span>
              </div>
            ) : (
              <Icon name="Circle" size={16} className="text-muted-foreground" />
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-800">Detalles de pago</span>
            {paymentData?.amount ? (
              <Icon name="CheckCircle" size={16} className="text-success" />
            ) : (
              <Icon name="Circle" size={16} className="text-muted-foreground" />
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-800">Comprobante</span>
            {uploadedFile ? (
              <Icon name="CheckCircle" size={16} className="text-success" />
            ) : (
              <Icon name="Circle" size={16} className="text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {(!canProceed) && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-900 rounded">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-yellow-700 mt-0.5" />
            <div className="text-sm">
              <strong>No puedes procesar la compra:</strong>
              <div>Selecciona al menos 2 boletos o aumenta la cantidad a 2.</div>
            </div>
          </div>
        </div>
      )}

      {serverError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-900 rounded">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-700 mt-0.5" />
            <div className="text-sm">
              <strong>Error:</strong>
              <div>{serverError}</div>
            </div>
          </div>
        </div>
      )}

      <Button
        variant="secondary"
        size="lg"
        fullWidth
        onClick={handleProcessPurchase}
        disabled={!canProceed || isProcessing || parentProcessing}
        loading={isProcessing || parentProcessing}
        iconName="CreditCard"
        iconPosition="left"
        className="mb-4"
      >
        {(isProcessing || parentProcessing) ? 'Procesando...' : 'Procesar Compra'}
      </Button>

      <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Shield" size={14} className="text-accent mt-0.5" />
          <div>
            <p className="text-xs text-gray-800 font-caption">
              Tu información está protegida con encriptación SSL. 
              Recibirás confirmación por email y WhatsApp una vez verificado el pago.
            </p>
          </div>
        </div>
      </div>



      {/* POPUP DE ÉXITO */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-border p-8 max-w-md w-full text-center shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Check" size={40} className="text-success" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">¡Compra Exitosa!</h2>
            <p className="text-gray-400 mb-6">
              Tus boletos: <span className="font-mono text-secondary">
                {Array.from(selectedTickets).join(', ')}
              </span> han sido reservados.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Tu comprobante está en revisión. Te notificaremos por WhatsApp.
            </p>
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => {
                setShowSuccessPopup(false);
                // REFRESCAR LA PÁGINA UNA VEZ
                window.location.reload();
              }}
            >
              Entendido
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export default PurchaseSummary;